import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { BackdropOverlay } from '@/components/ui/backdrop-overlay';
import { BackdropProvider } from '@/context/BackdropContext';
import { useRtl } from '@/context/RtlContext';
import {
  DangerNotificationProvider,
  DialogNotificationProvider,
  NotificationProvider,
} from '@/contexts/notification';
import { useOpenGlobalSearch } from '@/hooks/useOpenGlobalSearch.tsx';
import { useUpdateClientsCache } from '@/hooks/useUpdateClients.tsx';
import { UseUpdateItemsCache } from '@/hooks/useUpdateItems.tsx';
import { cn } from '@/lib/utils';
import { useSidebarStore } from '@/store/useSidebarStore';
import { useCheckPrems } from '@/utils/premissions/checkPrems';
import Premission from '@/utils/premissions/premission.type';

// Import performance optimization components
import {
  MainLayoutPreloader,
  PerformanceHints,
  ServiceWorkerProvider,
} from '@/components/custom-ui/PreloadAssets';
import { FloatingOnlineWidget } from '@/components/custom-ui/FloatingOnlineWidget';

// Lazy load heavy components for better performance
const LazyEnterpriseNavbar = lazy(() =>
  import('./components/EnterpriseNavbar').then((module) => ({
    default: module.EnterpriseNavbar,
  })),
);

const LazyModernSidebar = lazy(() =>
  import('./components/ModernSidebar').then((module) => ({
    default: module.ModernSidebar,
  })),
);

// Lightweight loading components for lazy loaded elements with RTL support
const NavbarSkeleton = React.memo(({ isRtl }: { isRtl?: boolean }) => (
  <div className="fixed left-0 right-0 top-0 z-50 h-16 animate-pulse border-b border-slate-200/50 bg-white/80 backdrop-blur-sm dark:border-slate-800/50 dark:bg-slate-950/80">
    <div
      className={cn(
        'flex h-full items-center justify-between px-4',
        isRtl && 'flex-row-reverse',
      )}
    >
      <div className="h-6 w-32 rounded bg-slate-200 dark:bg-slate-700"></div>
      <div className="h-8 w-8 rounded-full bg-slate-200 dark:bg-slate-700"></div>
    </div>
  </div>
));

const SidebarSkeleton = React.memo(({ isRtl }: { isRtl?: boolean }) => (
  <div
    className={cn(
      'fixed top-0 z-30 h-full w-20 animate-pulse border border-slate-200/50 bg-white/80 backdrop-blur-sm dark:border-slate-800/50 dark:bg-slate-950/80',
      isRtl ? 'right-0 border-l border-r-0' : 'left-0 border-l-0 border-r',
    )}
  >
    <div className="space-y-4 p-4">
      <div className="h-8 w-8 rounded bg-slate-200 dark:bg-slate-700"></div>
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-8 w-8 rounded bg-slate-200 dark:bg-slate-700"
        ></div>
      ))}
    </div>
  </div>
));

// Performance isolation wrapper - prevents unnecessary re-renders
const PerformanceIsolationWrapper = React.memo(
  function PerformanceIsolationWrapper({
    children,
  }: {
    children: React.ReactNode;
  }) {
    return <>{children}</>;
  },
  () => true, // Never re-render - complete isolation
);

// Stable background hooks component - completely isolated from re-renders
const StableBackgroundHooks = React.memo(
  function StableBackgroundHooks() {
    UseUpdateItemsCache();
    useUpdateClientsCache();
    useOpenGlobalSearch();
    return null;
  },
  () => true, // Never re-render for maximum performance
);

// Optimized responsive hook with debouncing for better performance on old PCs
const useOptimizedResponsive = () => {
  const [isMobile, setIsMobile] = useState(() => {
    // Early return for SSR/initial render
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
  });

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const handleResize = () => {
      // Clear previous timeout for debouncing
      clearTimeout(timeoutId);

      // Debounce resize events for better performance on slow devices
      timeoutId = setTimeout(() => {
        const newIsMobile = window.innerWidth < 768;
        // Only update if value actually changed to prevent unnecessary re-renders
        setIsMobile((prev) => (prev !== newIsMobile ? newIsMobile : prev));
      }, 150); // 150ms debounce for old PCs
    };

    // Use passive listener for better scroll performance
    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return isMobile;
};

// Highly optimized main content with CSS transforms for better performance
const OptimizedMainContent = React.memo(function OptimizedMainContent({
  children,
  isRtl,
  isMobile,
  isFullscreen,
}: {
  children: React.ReactNode;
  isRtl: boolean;
  isMobile: boolean;
  isFullscreen: boolean;
}) {
  // Ultra-stable class computation using CSS transforms for hardware acceleration
  const contentAreaClass = useMemo(() => {
    // Fullscreen mode — take entire viewport, no offsets
    if (isFullscreen) {
      return [
        'fixed inset-0',
        'will-change-transform',
        'transform-gpu',
        'transition-all duration-200 ease-out',
      ].join(' ');
    }

    const baseClasses = [
      'fixed top-16 bottom-0',
      'bg-gradient-to-br from-slate-50/50 to-slate-100/30',
      'dark:from-slate-950/50 dark:to-slate-900/30',
      'will-change-transform', // Hint for hardware acceleration
      'transform-gpu', // Force GPU acceleration
      'transition-all duration-300 ease-out',
    ].join(' ');

    // Mobile layout - full width with transform
    if (isMobile) {
      return `${baseClasses} left-0 right-0 translate-x-0`;
    }

    // Desktop layout - optimized positioning with transforms for RTL
    const sidebarOffset = isRtl
      ? 'left-0 right-20 translate-x-0' // RTL: full left, right margin for sidebar
      : 'left-20 right-0 translate-x-0'; // LTR: left margin for sidebar, full right

    return `${baseClasses} ${sidebarOffset}`;
  }, [isRtl, isMobile, isFullscreen]);

  // Ultra-stable scrollable content with performance optimizations
  const scrollableContentClass = useMemo(
    () =>
      [
        'h-full w-full',
        'overflow-y-auto overflow-x-hidden',
        'scroll-smooth',
        'scrollbar-none [&::-webkit-scrollbar]:hidden',
        'transform-gpu', // Hardware acceleration for scrolling
        'contain-layout contain-style', // CSS containment for better performance
      ].join(' '),
    [],
  );

  return (
    <main className={contentAreaClass} dir={isRtl ? 'rtl' : 'ltr'}>
      <div className={scrollableContentClass}>
        <PerformanceIsolationWrapper>{children}</PerformanceIsolationWrapper>
      </div>
    </main>
  );
});

// Highly optimized navbar wrapper with error boundary
const OptimizedNavbar = React.memo(function OptimizedNavbar({
  isRtl,
  isMobile,
  title,
}: {
  isRtl: boolean;
  isMobile: boolean;
  title?: string;
}) {
  return (
    <Suspense fallback={<NavbarSkeleton isRtl={isRtl} />}>
      <LazyEnterpriseNavbar title={title} isRtl={isRtl} isMobile={isMobile} />
    </Suspense>
  );
});

// Highly optimized sidebar wrapper with error boundary
const OptimizedSidebar = React.memo(function OptimizedSidebar({
  isRtl,
  isMobile,
}: {
  isRtl: boolean;
  isMobile: boolean;
}) {
  // Don't render sidebar on mobile for better performance
  if (isMobile) return null;

  return (
    <Suspense fallback={<SidebarSkeleton isRtl={isRtl} />}>
      <LazyModernSidebar isRtl={isRtl} isMobile={isMobile} />
    </Suspense>
  );
});

// Main layout interface
interface MainLayoutProps {
  children: React.ReactNode;
  title?: string;
}

// Ultra-high performance main layout component with full RTL support
export function MainLayout({ children, title }: MainLayoutProps) {
  const { isRtl } = useRtl();
  const isMobile = useOptimizedResponsive();
  const canViewOnlineUsers = useCheckPrems(Premission.Prem_Employee_Get_ALL);
  const isFullscreenMode = useSidebarStore((s) => s.isFullscreenMode);

  // Ultra-stable wrapper class computation with GPU acceleration hints
  const wrapperClass = useMemo(
    () =>
      cn(
        'relative min-h-screen bg-background',
        'transform-gpu', // Force GPU acceleration
        'contain-layout contain-style', // CSS containment for isolation
        // Enhanced backdrop for glass morphism with hardware acceleration
        'before:fixed before:inset-0 before:bg-gradient-to-br before:from-slate-50/30 before:via-transparent before:to-slate-100/20',
        'dark:before:from-slate-950/30 dark:before:to-slate-900/20',
        'before:pointer-events-none before:z-0 before:transform-gpu',
      ),
    [], // Completely stable - never changes
  );

  // Ultra-stable render function with maximum performance optimizations
  const renderContent = useCallback(() => {
    return (
      <div className={wrapperClass} dir={isRtl ? 'rtl' : 'ltr'}>
        {/* Performance Optimization Components */}
        <MainLayoutPreloader />
        <PerformanceHints />
        <ServiceWorkerProvider />

        {/* Background Hooks - Completely Isolated for Zero Re-renders */}
        <StableBackgroundHooks />

        {/* Optimized Navigation Components with Lazy Loading and RTL Support */}
        {!isFullscreenMode && <OptimizedNavbar isRtl={isRtl} isMobile={isMobile} title={title} />}
        {!isFullscreenMode && <OptimizedSidebar isRtl={isRtl} isMobile={isMobile} />}

        {/* Ultra-Optimized Main Content Area with RTL Support */}
        <OptimizedMainContent isRtl={isRtl} isMobile={isMobile} isFullscreen={isFullscreenMode}>
          {children}
        </OptimizedMainContent>

        {/* Static Overlay Components - Optimized for low-resource PCs */}
        {!isFullscreenMode && <BackdropOverlay />}

        {/* Online Users Widget - Shows connected users (requires permission) */}
        {canViewOnlineUsers && !isFullscreenMode && <FloatingOnlineWidget />}
      </div>
    );
  }, [wrapperClass, isRtl, isMobile, title, children, canViewOnlineUsers, isFullscreenMode]);

  // Wrap with notification providers for enterprise features
  // BackdropProvider should be outside to avoid context issues
  return (
    <BackdropProvider>
      <NotificationProvider>
        <DangerNotificationProvider>
          <DialogNotificationProvider>
            {renderContent()}
          </DialogNotificationProvider>
        </DangerNotificationProvider>
      </NotificationProvider>
    </BackdropProvider>
  );
}
